import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AssessmentOutlined,
  ArrowOutwardOutlined,
  FactoryOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  MoveUpOutlined,
  PlaylistAddCheckCircleOutlined,
  ReceiptLongOutlined,
  ReplayOutlined,
  RuleFolderOutlined,
  ShoppingCartOutlined,
  WarehouseOutlined,
} from '@mui/icons-material';

import { Header } from '@/__core/layout';
import InventoryPermissionGuard from './InventoryPermissionGuard';
import { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import { getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import { INVENTORY_SECTIONS } from '@/modules/inventory/config/inventoryConfig';
import { InventorySectionKey } from '@/modules/inventory/types';
import { RootState } from '@/setup/redux/RootReducer';

type DashboardSummaryCard = {
  label: string;
  value: string;
  tone?: string;
};

const emptyDashboardSummary: Record<InventorySectionKey | 'dashboard' | 'catalogs', DashboardSummaryCard[]> = {
  dashboard: [],
  catalogs: [],
  requests: [],
  purchasing: [],
  receipts: [],
  allocation: [],
  issues: [],
  returns: [],
  stocktakes: [],
  reports: [],
};

const sectionIcons: Record<string, React.ReactNode> = {
  requests: <PlaylistAddCheckCircleOutlined sx={{ fontSize: 20 }} />,
  purchasing: <ShoppingCartOutlined sx={{ fontSize: 20 }} />,
  receipts: <ReceiptLongOutlined sx={{ fontSize: 20 }} />,
  allocation: <MoveUpOutlined sx={{ fontSize: 20 }} />,
  issues: <LocalShippingOutlined sx={{ fontSize: 20 }} />,
  returns: <ReplayOutlined sx={{ fontSize: 20 }} />,
  stocktakes: <WarehouseOutlined sx={{ fontSize: 20 }} />,
  reports: <AssessmentOutlined sx={{ fontSize: 20 }} />,
  catalogs: <RuleFolderOutlined sx={{ fontSize: 20 }} />,
};

export default function InventoryDashboardModule() {
  const navigate = useNavigate();
  const companies = useSelector((state: RootState) => state.auth.companies);
  const loginCompany = getSelectedCompany();

  const companyOptions: CompanyData[] = companies;
  const [viewCompanyId, setViewCompanyId] = useState(
    () => loginCompany?.company_id || companyOptions[0]?.company_id || ''
  );

  const operationalSections = useMemo(
    () => INVENTORY_SECTIONS.filter((section) => !['dashboard', 'catalogs'].includes(section.key)),
    []
  );

  const highlightCards = emptyDashboardSummary.dashboard;
  const catalogHealthCards = emptyDashboardSummary.catalogs;

  const handleCompanyChange = (companyId: string) => {
    setViewCompanyId(companyId);
  };

  return (
    <InventoryPermissionGuard
      permissions={[
        { resource: 'inventory.item', action: 'read' },
        { resource: 'inventory.stockbalance', action: 'read' },
        { resource: 'inventory.stocktransaction', action: 'read' },
        { resource: 'inventory.store', action: 'read' },
        { resource: 'inventory.materialrequest', action: 'read' },
        { resource: 'inventory.receipt', action: 'read' },
        { resource: 'inventory.issue', action: 'read' },
        { resource: 'inventory.stocktake', action: 'read' },
      ]}
    >
      <>
      <Header />

      <Box sx={{ p: { xs: 2, md: 3 }, background: 'linear-gradient(180deg, #f8fafc 0%, #eef6f3 100%)', minHeight: 'calc(100vh - 64px)' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 4,
            border: '1px solid #dbe7e1',
            background: 'linear-gradient(135deg, #0f172a 0%, #14532d 100%)',
            color: '#f8fafc',
            mb: 3,
          }}
        >
          <Grid container spacing={3} alignItems='center'>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Stack spacing={1.5}>
                <Chip
                  icon={<Inventory2Outlined sx={{ color: '#86efac !important' }} />}
                  label='Inventory Control Center'
                  sx={{
                    width: 'fit-content',
                    color: '#dcfce7',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                />
                <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.3rem' }, fontWeight: 700, lineHeight: 1.15 }}>
                  Dashboard inventory cần phản ánh luồng vận hành, không phải CRUD shell.
                </Typography>
                <Typography sx={{ color: 'rgba(248,250,252,0.78)', maxWidth: 780, fontSize: '0.98rem' }}>
                  Theo dõi nhanh điểm nghẽn mua hàng, nhập kho, allocation, xuất kho, hoàn trả và kiểm kê.
                  Từ đây người dùng đi vào từng luồng nghiệp vụ thay vì thao tác trực tiếp trên một danh sách chung.
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.12)',
                  backgroundColor: 'rgba(15, 23, 42, 0.28)',
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(248,250,252,0.7)', mb: 1 }}>
                  Công ty đang xem
                </Typography>
                <TextField
                  select
                  size='small'
                  fullWidth
                  value={viewCompanyId}
                  onChange={(event) => handleCompanyChange(event.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#f8fafc',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    },
                    '& .MuiSvgIcon-root': { color: '#f8fafc' },
                  }}
                >
                  {companyOptions.map((company) => (
                    <MenuItem key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction='row' spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                  <Chip size='small' icon={<FactoryOutlined sx={{ fontSize: 16 }} />} label='Mock metrics' sx={{ color: '#dcfce7', backgroundColor: 'rgba(34,197,94,0.14)' }} />
                  <Chip size='small' label='Không có tab +' sx={{ color: '#e2e8f0', backgroundColor: 'rgba(148,163,184,0.16)' }} />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {highlightCards.map((card: DashboardSummaryCard) => (
            <Grid size={{ xs: 12, sm: 6, xl: 3 }} key={card.label}>
              <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #dbe4ea', height: '100%' }}>
                <Typography sx={{ color: '#64748b', fontSize: '0.84rem', mb: 1 }}>{card.label}</Typography>
                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: '#0f172a', mb: 1 }}>{card.value}</Typography>
                <Box sx={{ width: 54, height: 6, borderRadius: 999, backgroundColor: card.tone }} />
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, xl: 8 }}>
            <Paper sx={{ p: 2.5, borderRadius: 4, border: '1px solid #dbe4ea' }}>
              <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>Luồng vận hành inventory</Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>Điểm vào nhanh cho từng nghiệp vụ chính cùng các chỉ số mock gần nhất.</Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                {operationalSections.map((section) => {
                  const summary = emptyDashboardSummary[section.key];
                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={section.key}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          height: '100%',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                          <Stack direction='row' spacing={1.25}>
                            <Box sx={{ width: 40, height: 40, borderRadius: 2.5, backgroundColor: '#ecfdf5', color: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {sectionIcons[section.key]}
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{section.title}</Typography>
                              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.25 }}>{section.description}</Typography>
                            </Box>
                          </Stack>
                          <Tooltip title='Mở màn hình'>
                            <IconButton
                              size='small'
                              onClick={() => navigate(section.path)}
                              sx={{
                                border: '1px solid #cbd5e1',
                                borderRadius: 2,
                                color: '#334155',
                                backgroundColor: '#fff',
                                '&:hover': {
                                  backgroundColor: '#f8fafc',
                                  borderColor: '#94a3b8',
                                },
                              }}
                            >
                              <ArrowOutwardOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Grid container spacing={1.25} sx={{ mt: 1.5 }}>
                          {summary.slice(0, 4).map((metric: DashboardSummaryCard) => (
                            <Grid size={{ xs: 6 }} key={metric.label}>
                              <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: '#f8fafc', border: '1px solid #edf2f7', height: '100%' }}>
                                <Typography sx={{ fontSize: '0.76rem', color: '#64748b', minHeight: 32 }}>{metric.label}</Typography>
                                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', mt: 0.5 }}>{metric.value}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, xl: 4 }}>
            <Stack spacing={3}>
              <Paper sx={{ p: 2.5, borderRadius: 4, border: '1px solid #dbe4ea' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.02rem', color: '#0f172a', mb: 1.5 }}>Sức khỏe danh mục</Typography>
                <Stack spacing={1.25}>
                  {catalogHealthCards.map((card: DashboardSummaryCard) => (
                    <Box key={card.label} sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: '#f8fafc', border: '1px solid #edf2f7' }}>
                      <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography sx={{ fontSize: '0.86rem', color: '#334155' }}>{card.label}</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{card.value}</Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <Button variant='text' onClick={() => navigate('/inventory/items')} sx={{ textTransform: 'none', mt: 1.5, px: 0 }}>
                  Đi tới danh mục vật tư
                </Button>
              </Paper>

              <Paper sx={{ p: 2.5, borderRadius: 4, border: '1px solid #dbe4ea' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.02rem', color: '#0f172a', mb: 1.5 }}>Radar vận hành</Typography>
                <Stack spacing={1.25}>
                  <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
                    <Typography sx={{ fontWeight: 600, color: '#9a3412', fontSize: '0.9rem' }}>3 luồng cần chú ý ngay</Typography>
                    <Typography sx={{ color: '#7c2d12', fontSize: '0.84rem', mt: 0.5 }}>
                      Mua hàng chờ duyệt, nhập kho chờ ghi sổ và allocation đang treo đang là 3 điểm nghẽn lớn nhất theo mock data hiện tại.
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2.5, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <Typography sx={{ fontWeight: 600, color: '#1d4ed8', fontSize: '0.9rem' }}>Điểm vào nhanh</Typography>
                    <Stack direction='row' spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Button size='small' variant='outlined' onClick={() => navigate('/inventory/requests')} sx={{ textTransform: 'none' }}>Request</Button>
                      <Button size='small' variant='outlined' onClick={() => navigate('/inventory/purchasing')} sx={{ textTransform: 'none' }}>PO</Button>
                      <Button size='small' variant='outlined' onClick={() => navigate('/inventory/receipts')} sx={{ textTransform: 'none' }}>Nhập kho</Button>
                      <Button size='small' variant='outlined' onClick={() => navigate('/inventory/stocktakes')} sx={{ textTransform: 'none' }}>Kiểm kê</Button>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      </>
    </InventoryPermissionGuard>
  );
}